

import { Component, Vue } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import cloneDeep from "lodash/cloneDeep"

import { Team, DashboardSummary } from "@/types"

import api from "@/services/api"
import dashboardApi from "@/services/api.dashboard"

import { BModal } from "bootstrap-vue"
import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import ViewFolders from "@/components/Folders/ViewFolders.vue"
import UserList from "@/components/Config/Users/UserList.vue"
import DataModal from "@/components/Config/DataModal.vue"
import ScrollTabs from "@/components/Controls/Tabs/ScrollTabs.vue"
import ScrollTab from "@/components/Controls/Tabs/ScrollTab.vue"
import { CurrentUser, ExtendedPermission } from "@/types/user"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"

import StringValidation from "@/scripts/stringValidation"
import { EditDataPermissionTitles } from "@/types/permission"
import { TenantFeatures } from "@/types/tenantFeatures"
import { Folder, FolderType } from "@/types/folder"
import FolderService from "@/services/folder-service"
const setup = namespace("setup")

@Component({
	components: {
		"page": PageWrapper,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-modal": DataModal,
		"user-list": UserList,
		"scroll-tab": ScrollTab,
		"scroll-tabs": ScrollTabs,
		"view-folders": ViewFolders
	}
})
export default class UserSetup extends Vue {
	$refs!: { dataModal: BModal }

	@setup.Action
	public loadTeams!: () => Promise<void>;

	@Getter("getMe")
	public me!: CurrentUser;

	@setup.Getter("getTeams")
	public teams!: Team[];

	selectedTeam: Team|null = null

	@setup.Action
	public getADEnabled!: () => Promise<boolean>

	@Getter("getIsSuiteEnabled") isSuiteEnabled! : boolean;
	@Getter("getIsFeatureEnabled") isFeatureEnabled!: (feature: TenantFeatures) => boolean

	folders: Folder [] = []

	private isLoadingOnMount = true
	private isLoadingData = false

	currentTeamDashboards: DashboardSummary[] = []
	extendedPermissions: ExtendedPermission[] = []

	folderService = new FolderService()

	tenantFeature = TenantFeatures.TeamFolders

	public filter: string = "";
	public adEnabled: boolean = false;

	public async mounted (): Promise<void> {
		api.extendedPermissionsGet().then(permissions => {
			// Only show permissions that are don't have a feature, or have their feature enabled
			permissions = permissions.filter(p => !p.requiredFeature || this.isFeatureEnabled(p.requiredFeature))
			this.extendedPermissions = (permissions || [])
		})

		this.adEnabled = await this.getADEnabled()
		await this.load()
		this.isLoadingOnMount = false
	}

	public async load () {
		this.isLoadingData = true
		await this.folderService.load(this.me, FolderType.Team)
		await this.loadTeams()
		this.isLoadingData = false
	}

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title),
				readonly: (item: any) => item.preventEdit || this.isSuiteEnabled
			},
			{
				key: "defaultDashboardId",
				label: "Default Dashboard",
				valueColumn: "defaultDashboardId",
				sortable: true,
				dataType: "list",
				options: this.currentTeamDashboards,
				inTable: false
			},
			{
				key: "editData",
				label: "Edit Data",
				sortable: true,
				dataType: "checkbox",
				inModal: (item: any) => !item.preventEdit
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.folderService.currentUser ? this.folderService.getPermittedSelectionFolders() : [],
				sortable: true,
				inTable: false,
				inModal: () => this.isFeatureEnabled(this.tenantFeature)
			},
			{
				key: "editDataPermissions",
				label: "Edit Data Permissions",
				dataType: "list-multiple",
				placeholder: "All edit permissions",
				options: EditDataPermissionTitles.filter(p => !p.tenantFeature || this.isFeatureEnabled(p.tenantFeature!)),
				inModal: (item: any) => !item.preventEdit &&
					item.editData &&
					this.isFeatureEnabled(TenantFeatures.EditDataPermissionBreakdown),
				inTable: false
			},
			{
				key: "editUsers",
				label: "Edit Users",
				sortable: true,
				dataType: "checkbox",
				inModal: (item: any) => !item.preventEdit
			},
			{
				key: "editInsights",
				label: "Edit Insights",
				sortable: true,
				dataType: "checkbox",
				inModal: (item: any) => !item.preventEdit
			},
			...this.extendedPermissionColumns,
			{
				key: "adGroup",
				label: "Active Directory Group",
				inTable: false,
				inModal: (item: any) => this.adEnabled
			}
		]
	}

	public get extendedPermissionColumns () {
		return this.extendedPermissions
			? this.extendedPermissions
				.filter(permission => !permission.requiredFeature || this.isFeatureEnabled(permission.requiredFeature))
				.map(permission => {
					return {
						key: "extendedPermissions." + permission.key,
						label: permission.title,
						description: permission.description,
						dataType: "checkbox",
						inModal: (item: any) => !item.preventEdit
					}
				})
			: []
	}

	public get filteredTeams () {
		this.folderService.loadFolders()
		if (!this.isFeatureEnabled(this.tenantFeature)) {
			this.teams.filter(team => team.folder !== null).forEach(team => team.folder = null)
		}

		if (this.filter === "") {
			return this.teams
		}
		const lowerFilter = this.filter.toLowerCase()
		return this.teams.filter(team => team.title.toLowerCase().indexOf(lowerFilter) > -1)
	}

	private get isLoading () {
		return this.isLoadingOnMount || this.isLoadingData
	}

	public async save (team: Team): Promise<void> {
		try {
			await api.teamSave(team)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error saving Role:  ${team.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		await this.load()
		await this.selectTeam(this.selectedTeam)
	}

	public async createNewTeam (): Promise<void> {
		this.currentTeamDashboards = await dashboardApi.dashboardGetListForTeam("")
		this.$refs.dataModal.open({
		})
	}

	public async editTeam (team: Team): Promise<void> {
		this.currentTeamDashboards = await dashboardApi.dashboardGetListForTeam(team.id)
		this.$refs.dataModal.open(cloneDeep(team))
	}

	public async deleteTeam (team: any): Promise<void> {
		try {
			await api.teamDelete(team.id)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error deleting Role: ${team.title}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		await this.loadTeams()
	}

	public async selectTeam (team: Team | null) {
		this.selectedTeam = team ? this.filteredTeams.firstOrDefault(f => f.id === team.id) : null
		await this.loadTeams()
	}

	private async folderDeleted (folder: Folder) {
		await this.load()
	}
}

